import { useRef } from 'react';
import { useAtomValue } from 'jotai';
import { MapPin } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import {
    _tileMediaVariants,
    TileMedia,
} from '@rocco/ui/tile-shared/components/TileMedia';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { TileFollowButton } from '@rocco/ui/tile-shared/components/TileFollowButton';
import { ProfessionalTileLogo } from './ProfessionalTileLogo';
import { ProfessionalTileServices } from './ProfessionalTileServices';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import { useTracker } from '../../../../../tracker';
import { trackTileMediaDetailClick } from '@archipro-website/web/app/modules/tracking/util/trackTileMedia';
import { OptionalLink } from '@rocco/components/link/OptionalLink';
import { TILE_COMPONENTS_CLASS_NAMES } from '@rocco/ui/tile-shared/utils/contstants';
import { usePrefersReducedMotion } from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';
import { uiContextAtomViewTransitionsEnabled } from '@rocco/states/uiContextAtomViewTransitions';
import { useTileAppModeScope } from '@rocco/contexts/TileAppModeScopeContext';

export const ProfessionalTile = (
    props: TileViewModel<ProfessionalTileViewModel> & {
        hideServiceTags?: boolean;
        hideLocation?: boolean;
        rounded?: boolean;
    },
) => {
    const {
        aspectRatio,
        model: professional,
        section,
        enableTileSlider,
        isFollowed,
        onFollow,
        hideFollowButton = false,
        followButton,
        overlay,
        captionAction,
        compact = false,
        hideServiceTags = false,
        hideLocation = false,
        rounded = true,
        inlineActionButtons,
        disableLink = false,
        onClick,
        className,
        contentEditable,
        eager,
        imageSizes,
    } = props as TileViewModel<ProfessionalTileViewModel> & {
        hideCategory?: boolean;
        rounded?: boolean;
    };

    // 5.0 directory browse: the portrait tile shows the larger detail-page
    // typography (22px name + category) rather than the default 16px.
    const isPortrait = aspectRatio === 'portrait';
    const tileActions = useAtomValue(uiContextAtomTileActions);
    const tileRef = useRef<HTMLAnchorElement>(null);
    const isSponsored = professional.services.includes('Sponsored');
    const tracker = useTracker();
    // Professionals sit out the tile→detail hero morph: the profile page
    // opens on its own intro reel, so a zoomed tile would land under an
    // animation that immediately replaces it. The tile navigates plainly and
    // only the mobile app-mode chrome below participates — that stays a
    // listing-to-detail presentation, so moving between detail pages does
    // not bury the site header behind a back stack.
    const viewTransitionsFlagOn = useAtomValue(
        uiContextAtomViewTransitionsEnabled,
    );
    const inAppModeScope = useTileAppModeScope();
    // Reduced motion opts out of the app-mode chrome too, exactly like a
    // plain flag-off navigation.
    const prefersReducedMotion = usePrefersReducedMotion();
    const appModeEnabled =
        viewTransitionsFlagOn && inAppModeScope && !prefersReducedMotion;

    const handleTileClick = () => {
        onClick?.();
        if (disableLink) {
            return;
        }
        tileActions?.trackingConfig?.onClick?.(
            tileRef,
            professional.id,
            professional.id,
            section,
        );

        trackTileMediaDetailClick(tracker, {
            itemID: professional.id,
            itemType: 'ProfessionalTile',
            itemName: professional.title,
        });
    };

    return (
        <OptionalLink
            ref={tileRef}
            mode={disableLink ? 'override' : 'normal'}
            prefetch="intent"
            to={professional.link}
            state={appModeEnabled ? { detailTransition: true } : undefined}
            onClick={handleTileClick}
        >
            <div
                className={cn(
                    'flex flex-col',
                    isPortrait ? 'gap-2.5' : 'gap-4',
                    className,
                )}
            >
                <div
                    className={cn(
                        // @container makes this wrapper a fixed captor again,
                        // but the anchor helper detects container-type and
                        // subtracts it back out.
                        'group @container relative',
                        // Sizing only: this wrapper hosts the save overlay,
                        // whose fixed anchor must stay positioned against the
                        // viewport. TileMedia below is still contained.
                        _tileMediaVariants({
                            aspectRatio,
                            rounded,
                            contained: false,
                        }),
                    )}
                >
                    <TileMedia
                        aspectRatio={aspectRatio}
                        images={[professional.image]}
                        video={professional.video}
                        enableTileSlider={enableTileSlider}
                        rounded={rounded}
                        eager={eager}
                        sizes={imageSizes}
                        onSlideChange={(direction) => {
                            tileActions?.trackingConfig?.onTileMediaSliderSlideChange?.(
                                direction,
                                professional.id,
                            );
                        }}
                    />

                    {overlay ? (
                        overlay
                    ) : (
                        <>
                            <div
                                className={cn(
                                    'absolute inset-0 hidden bg-gray-900/10 opacity-0 transition-opacity duration-300 md:block md:group-hover:opacity-100',
                                    rounded && 'rounded-tile',
                                )}
                            />
                            <div className="absolute top-3.5 right-4 flex gap-1 transition-opacity duration-300 md:invisible md:opacity-0 md:group-hover:visible md:group-hover:opacity-100">
                                {followButton
                                    ? followButton
                                    : !hideFollowButton && (
                                          <TileFollowButton
                                              pinId={professional.id}
                                              pinType="ProfessionalPin"
                                              isFollowed={isFollowed}
                                              onFollow={onFollow}
                                          />
                                      )}
                            </div>
                        </>
                    )}

                    {professional.logo && (
                        // Above the hover chrome. The overlay's scrim and its
                        // buttons take z-10, and the badge is the practice's
                        // own mark — dimming it under a wash on hover reads as
                        // the tile going out rather than lighting up.
                        //
                        // Only the legacy board-dropdown overlay puts a
                        // control in this corner, so only there does the badge
                        // step aside on hover rather than sit over it. The
                        // action panel that replaces it keeps Save in the
                        // top-right and its other actions along the bottom
                        // edge, leaving this corner free — the badge stays.
                        // `data-tile-refresh` on the document element is how
                        // the tile refresh reaches CSS that cannot take a prop
                        // (it also carries `--radius-tile`); without it, a
                        // passed-in `overlay` is the legacy one. The fallback
                        // chrome below keeps its one button on the right and
                        // needs no room either way.
                        //
                        // The badge takes no clicks in any case: it is a mark,
                        // not a button, and the tile's own link is the thing
                        // beneath it.
                        <div
                            className={cn(
                                'pointer-events-none absolute top-2 left-2.5 z-20 md:top-4 md:left-4 @max-[240px]:[zoom:0.85] @max-[200px]:[zoom:0.7]',
                                overlay &&
                                    'transition-opacity duration-300 [html:not([data-tile-refresh])_&]:md:group-hover:opacity-0',
                            )}
                        >
                            <ProfessionalTileLogo
                                image={professional.logo.image}
                                background={professional.logo.background}
                                compact={compact}
                                // 5.0 browse tile uses a larger 100px logo
                                // badge. It is sized for the 3-4 column tile;
                                // the wrapper's container zoom shrinks it in
                                // step with the narrow Display tiles, where
                                // the fixed badge crowded out the Save pill.
                                className={isPortrait ? 'size-25' : undefined}
                            />
                        </div>
                    )}
                    {isSponsored && (
                        <div className="text-md absolute right-0 mt-3 text-gray-900/40">
                            <span>Sponsored</span>
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-4">
                    {inlineActionButtons && (
                        <div className="flex flex-col">
                            {inlineActionButtons}
                        </div>
                    )}
                    {/* The caption's row: the name and category, and the
                        host's control — the mobile ••• — on its right. */}
                    <div className="flex items-start gap-1">
                        <div
                            className={cn(
                                TILE_COMPONENTS_CLASS_NAMES.TILE_DESCRIPTION_BLOCK,
                                'flex min-w-0 flex-1 flex-col md:gap-0.5',
                            )}
                        >
                            <span
                                contentEditable={contentEditable?.enabled.title}
                                suppressContentEditableWarning={true}
                                onInput={(
                                    e: React.FormEvent<HTMLSpanElement>,
                                ) =>
                                    contentEditable?.onChange?.(
                                        (e.target as HTMLSpanElement)
                                            .textContent ?? '',
                                        'title',
                                    )
                                }
                                onBlur={() => {
                                    contentEditable?.onBlur?.();
                                }}
                                onKeyDown={(
                                    e: React.KeyboardEvent<HTMLSpanElement>,
                                ) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        (e.target as HTMLSpanElement).blur();
                                    }
                                }}
                                className={cn(
                                    isPortrait
                                        ? cn(
                                              // Below md let the name wrap to 2 lines
                                              // on the 2-up browse grid at the compact
                                              // design-board tile scale (reserving 2
                                              // lines' height so rows align); md+ is
                                              // single-line at the product tile's 16px,
                                              // so the three browse grids read alike.
                                              'overflow-hidden font-medium text-nowrap text-ellipsis text-[#323232] max-md:line-clamp-2 max-md:text-[0.75rem] max-md:leading-[1.2] max-md:whitespace-normal md:text-[1rem] md:leading-[1.3]',
                                          )
                                        : 'overflow-hidden text-sm font-medium tracking-[0.03rem] text-nowrap text-ellipsis md:h-[1.4rem] md:text-base md:tracking-[0.02rem]',
                                    contentEditable?.enabled.title &&
                                        'focus-within:border-primary-500 -mx-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 focus-within:text-gray-900 hover:border-gray-500',
                                )}
                            >
                                {professional.title}
                            </span>
                            <span
                                contentEditable={
                                    contentEditable?.enabled.description
                                }
                                suppressContentEditableWarning={true}
                                onInput={(
                                    e: React.FormEvent<HTMLSpanElement>,
                                ) =>
                                    contentEditable?.onChange?.(
                                        (e.target as HTMLSpanElement)
                                            .textContent ?? '',
                                        'description',
                                    )
                                }
                                onBlur={() => {
                                    contentEditable?.onBlur?.();
                                }}
                                onKeyDown={(
                                    e: React.KeyboardEvent<HTMLSpanElement>,
                                ) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        (e.target as HTMLSpanElement).blur();
                                    }
                                }}
                                className={cn(
                                    isPortrait
                                        ? cn(
                                              // Quiet meta scale below md, matching
                                              // the design-board card's "Created by"
                                              // grey; md+ tracks the name size.
                                              'overflow-hidden font-normal text-nowrap text-ellipsis max-md:text-[0.75rem] max-md:leading-[1.3] max-md:text-gray-500 md:text-[1rem] md:leading-[1.3] md:text-[#323232]/40',
                                          )
                                        : 'overflow-hidden text-sm font-medium tracking-[0.03rem] text-nowrap text-ellipsis text-gray-900/40 md:h-[1.4rem] md:text-base md:tracking-[0.02rem]',
                                    contentEditable?.enabled.description &&
                                        'focus-within:border-primary-500 -ml-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 focus-within:text-gray-900 hover:border-gray-500',
                                )}
                            >
                                {professional.category}
                            </span>
                            {professional.location &&
                                !isPortrait &&
                                !hideLocation && (
                                    <span className="mt-[4px] mb-[-7px] flex min-w-0 items-center gap-1 text-xs leading-[0.875rem] tracking-[0.03rem] text-[#000] md:mt-[2px] md:text-3 md:leading-[1rem] md:tracking-[0.02rem]">
                                        {professional.showLocationPin !==
                                            false && (
                                            <MapPin
                                                aria-hidden
                                                className="size-3 shrink-0 text-[#000]"
                                            />
                                        )}
                                        <span className="min-w-0 truncate">
                                            {professional.location}
                                        </span>
                                    </span>
                                )}
                        </div>
                        {/* `empty:hidden` because a host's control can render
                            nothing at some widths — the mobile ••• does above
                            md — and an empty flex child still takes the row's
                            gap, which would narrow every name by it. */}
                        {captionAction ? (
                            <div className="shrink-0 empty:hidden">
                                {captionAction}
                            </div>
                        ) : null}
                    </div>

                    {!hideServiceTags && !isPortrait && (
                        <>
                            <div className="hidden justify-start gap-2 md:flex">
                                <ProfessionalTileServices
                                    services={professional.services}
                                />
                            </div>
                            <div className="flex justify-start gap-2 md:hidden">
                                <ProfessionalTileServices
                                    limit={1}
                                    services={professional.services}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </OptionalLink>
    );
};
